import React from "react";
import SurplusDeficitGraph from "../Graphs/SurplusDeficitGraph";
import IncomeExpenditureGraph from "../Graphs/IncomeExpenditureGraph";
import PensionGraph from "../Graphs/PensionGraph";
import InvestmentGraph from "../Graphs/InvestmentGraph";
import StackedPensionGraph from "../Graphs/StackedPensionGraph";
import StackedInvestmentGraph from "../Graphs/StackedInvestmentGraph";
import CombinedIncomeExpenditureGraph from "../Graphs/CombinedIncomeExpenditure";
import CombinedSurplusDeficitGraph from "../Graphs/CombinedSurplusDeficitGraph";
import { useCfmContext } from "../../../Context/CfmContext";

/**
 * GraphPreRenderer is a React component that pre-renders various financial graphs for each person in the application's state.
 * 
 * The component renders graphs for pensions, investments, surplus/deficit, and income/expenditure for each detail in the state.
 * For the first person (if there are multiple), it also renders stacked and combined graphs.
 * 
 * The rendered graphs are positioned off-screen (using absolute positioning) to facilitate tasks such as printing or exporting,
 * without displaying them in the visible UI.
 * 
 * @function
 * @returns {JSX.Element} A hidden div containing all pre-rendered graph components for each person and combined/stacked graphs if applicable.
 */
const GraphPreRenderer = () => {
  const { state } = useCfmContext();
  const hasMultiple = (state.details || []).length > 1;

  return (
    <div
    style={{ position: "absolute", left: -9999, top: -9999 }}
    >
      {state.details.map((detail, index) => {
        const name = detail.name;
        const safeName = encodeURIComponent(name);
        const hasPension = (state.pensions || []).some((p) => p.name === name);
        const hasInvestment = (state.investments || []).some(
          (i) => i.name === name
        );
        const isFirst = index === 0;
        // console.log("Rendering graphs for:", name, "isFirst:", isFirst);

        return (
          <div key={name}>
            {hasPension && (
              <PensionGraph
                selectedPerson={name}
                isPrint={true}
                idOverride={`graph-${safeName}`}
              />
            )}
            {hasInvestment && (
              <InvestmentGraph
                selectedPerson={name}
                isPrint={true}
                idOverride={`inv-graph-${safeName}`}
              />
            )}
            <SurplusDeficitGraph
              selectedPerson={name}
              isPrint={true}
              idOverride={`sur-def-graph-${safeName}`}
            />
            <IncomeExpenditureGraph
              selectedPerson={name}
              isPrint={true}
              idOverride={`inc-expd-graph-${safeName}`}
            />

            {hasMultiple && isFirst && (
              <>
                <StackedPensionGraph
                  isPrint={true}
                  idOverride="stacked-pen-graph"
                />
                <StackedInvestmentGraph
                  isPrint={true}
                  idOverride="stacked-inv-graph"
                />
                <CombinedIncomeExpenditureGraph
                  isPrint={true}
                  idOverride="combined-inc-expd-graph"
                />
                <CombinedSurplusDeficitGraph
                  isPrint={true}
                  idOverride="combined-sur-def-graph"
                />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GraphPreRenderer;
